/**
 * Plain constants, types and helpers of the course content model. Free of zod
 * so the site bundle can use them without the validation schemas in index.ts.
 */

// ── Locales ──────────────────────────────────────────────────────────────────

export const LOCALES = ["nl", "en"] as const;
export type Locale = (typeof LOCALES)[number];

export type LocalizedText = Record<Locale, string>;

export const emptyText = (): LocalizedText => ({ nl: "", en: "" });

/** The requested locale, or the other one when the requested text is empty. */
export const pickText = (text: LocalizedText, locale: Locale): string => {
  const own = text[locale];
  if (own.trim()) return own;
  return text[locale === "nl" ? "en" : "nl"];
};

// ── Slugs ────────────────────────────────────────────────────────────────────

export const SLUG_REGEX = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** "Waarom bodemleven?" → "waarom-bodemleven" */
export const slugify = (value: string): string =>
  value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);

// ── Blocks ───────────────────────────────────────────────────────────────────

export const BLOCK_TYPES = [
  "heading",
  "paragraph",
  "list",
  "image",
  "callout",
  "quote",
  "video",
  "link",
  "quiz",
] as const;
export type BlockType = (typeof BLOCK_TYPES)[number];

export const CALLOUT_TONES = ["tip", "note", "warning"] as const;
export type CalloutTone = (typeof CALLOUT_TONES)[number];

// ── Courses ──────────────────────────────────────────────────────────────────

export const COURSE_STATUSES = ["draft", "published"] as const;
export type CourseStatus = (typeof COURSE_STATUSES)[number];

// ── Roles ────────────────────────────────────────────────────────────────────

/** Every role, lowest first; each role has every right of the roles before it. Stored in user.role. */
export const ROLES = ["learner", "analyst", "admin", "owner"] as const;
export type Role = (typeof ROLES)[number];

/** A course site's roles: no analyst, lab work exists only on the research site. */
export const COURSE_SITE_ROLES = ["learner", "admin", "owner"] as const satisfies readonly Role[];

/** What the role API may give; owners come only from the `role:set` CLI. */
export const ASSIGNABLE_ROLES = ["learner", "analyst", "admin"] as const satisfies readonly Role[];

/** A stored role as the app reads it: unknown or missing values get the default role's rights. */
export const toRole = (value: unknown): Role => ROLES.find((role) => role === value) ?? "learner";

const rank = (role: Role): number => ROLES.indexOf(role);

/** Whether `role` includes the rights of `minimum`. */
export const atLeast = (role: Role, minimum: Role): boolean => rank(role) >= rank(minimum);

/** Whether `role` ranks strictly above `other`: only such accounts are `role`'s to change. */
export const outranks = (role: Role, other: Role): boolean => rank(role) > rank(other);

/** The roles `actor` may give on a site, lowest first: the site's roles below the actor's, not owner. */
export const assignableRoles = (actor: Role, siteRoles: readonly Role[]): Role[] =>
  siteRoles.filter((role) => role !== "owner" && outranks(actor, role));

/** Rows per page of the users list (GET /api/admin/roles); the pager uses the same number. */
export const ACCOUNT_PAGE_SIZE = 25;

/**
 * Who manages a site's users: its lowest role that may give a role above the default there
 * (research: admin, who appoints analysts; course sites: owner, who appoints admins).
 */
export const managerRole = (siteRoles: readonly Role[]): Exclude<Role, "learner"> => {
  const manager = siteRoles.find((role) =>
    assignableRoles(role, siteRoles).some((given) => given !== "learner"),
  );
  if (manager === undefined || manager === "learner") {
    throw new Error("A site needs a role that can give roles");
  }
  return manager;
};

// ── Media uploads ────────────────────────────────────────────────────────────

export const MEDIA_MAX_BYTES = 5 * 1024 * 1024;
/** No SVG: it can carry script and is served from the site origin. */
export const MEDIA_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"] as const;
